import React, { useEffect, useState } from 'react';

interface GoogleMapsLoaderProps {
  apiKey: string;
  children: React.ReactNode;
  onLoad?: () => void;
}

declare global {
  interface Window {
    initGoogleMapsCallback?: () => void;
  }
}

const GoogleMapsLoader: React.FC<GoogleMapsLoaderProps> = ({ apiKey, children, onLoad }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // If Google Maps API is already loaded, don't load it again
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      if (onLoad) onLoad();
      return;
    }

    // Create a global callback function
    window.initGoogleMapsCallback = () => {
      setIsLoaded(true);
      if (onLoad) onLoad();
    };

    // Create script element
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMapsCallback`;
    script.async = true;
    script.defer = true;

    // Add the script to the document
    document.head.appendChild(script);

    // Set up a retry mechanism in case the callback doesn't fire
    const retryTimeout = setTimeout(() => {
      if (window.google?.maps) {
        console.log('Google Maps loaded but callback not fired, initializing manually');
        setIsLoaded(true);
        if (onLoad) onLoad();
      } else {
        console.log('Google Maps failed to load within timeout, retrying...');
        // Remove script and try again
        document.querySelector('#google-maps-script')?.remove();
        document.head.appendChild(script);
      }
    }, 5000);

    // Cleanup function
    return () => {
      if (window.initGoogleMapsCallback) {
        delete window.initGoogleMapsCallback;
      }
      clearTimeout(retryTimeout);
    };
  }, [apiKey, onLoad]);

  return <>{isLoaded ? children : <div className="h-full w-full flex items-center justify-center">Loading maps...</div>}</>;
};

export default GoogleMapsLoader; 